import { createCorsConfigSource } from './cors.ts';
import type { RouteConfig } from './route.ts';
import security, { type HttpSecurityConfig } from '../server/security/config.ts';
import { and, pattern, type ServerWebExchangeMatcher, upgradeMatcher } from '../server/util/matchers.ts';
import type { AuthorizationRule } from '../../types/auth';
import { MapUserDetailsService } from '../server/security/users/memory.ts';
import { UserBuilder, type UserDetails } from '../server/security/users/types.ts';
import { randomUUID } from 'node:crypto';
import { MAX_PASSWORD_LENGTH, type PasswordEncoder } from '../server/security/crypto/index.ts';
import getLogger from '../logger.ts';
import type { CorsConfigSource } from '../web/cors/types.ts';
import type { SecurityWebFilterChain } from '../server/security/web.ts';

const logger = getLogger('auth');

export function createSecurityConfig(context: RouteConfig): HttpSecurityConfig {
    const authorize: HttpSecurityConfig['authorize'] = [];
    const type = context.authConfig?.type;
    const defaultAccess: AuthorizationRule = { access: type !== 'none' ? 'authenticated' : 'permitted' };
    if (logger.enabledFor('info')) {
        logger.info(`using auth type: ${type ?? 'none'}, default access: ${defaultAccess.access}`);
    }
    for (const [path, route] of context.sockets) {
        const rule = route.authorize ?? defaultAccess;
        let matcher: ServerWebExchangeMatcher = pattern(path, { method: 'GET' });
        matcher = and([upgradeMatcher, matcher]);
        authorize.push([matcher, rule]);
    }
    authorize.push([pattern('/', { method: 'GET' }), { access: 'permitted' }]);
    authorize.push([pattern('/favicon.ico', {method: 'GET'}), { access: 'permitted' }]);
    authorize.push([pattern('/health', {method: 'GET'}), { access: 'permitted' }]);
    if (context.authorize.length > 0) {
        authorize.push(...context.authorize);
    }

    authorize.push(['any-exchange', defaultAccess]); // default rule for all other exchanges
    return {
        authorize,
        cors: {
            disabled: context.corsConfig === false,
        },
        x509: {
            disabled: type !== 'x509',
            ...context.authConfig?.x509,
        },
        basic: {
            disabled: type !== 'basic',
            ...context.authConfig?.basic,
        },
        jwt: {
            disabled: type !== 'oauth2',
            ...context.authConfig?.oauth2?.jwt
        }
    }
}

export function createUserDetailsService(context: RouteConfig) {
    // if (context.userDetailsService) {
    //     return context.userDetailsService;
    // }
    if (context.authConfig?.type === 'none') {
        return undefined;
    }

    function getOrDeducePassword(user: { password?: string }, encoder?: PasswordEncoder): string | null {
        if (context.authConfig?.type === 'x509') {
            return null;
        }
        let password = user.password;
        if (password === undefined) {
            // generate password
            const generatedPassword = randomUUID().replaceAll('-', '');
            if (logger.enabledFor('info')) {
                logger.info(`\n\n using generated password: ${generatedPassword}\n\nThis generated password is for development only. Your authentication configuration should be updated before running in production.\n`);
            }
            password = generatedPassword;
        }
        if (password.length > MAX_PASSWORD_LENGTH) {
            throw new Error(`Password length exceeds maximum length of ${MAX_PASSWORD_LENGTH} characters`);
        }
        if ((encoder !== undefined && encoder !== null) || /^\{.+}.*$/.test(password)) {
            return password;
        }
        return `{noop}${password}`;
    }

    const user = { name : 'dev-user', roles: [], ...(context.authConfig?.user) };
    const password = getOrDeducePassword(user);
    const roles = user.roles;
    const userDetails: UserDetails = UserBuilder.ofUsername(user.name).password(password).roles(...(roles)).build();
    return new MapUserDetailsService(userDetails);
}

export async function httpSecurity(context: RouteConfig): Promise<SecurityWebFilterChain> {
    const corsConfigSource: CorsConfigSource = createCorsConfigSource(context);
    const config = createSecurityConfig(context);
    const userDetailsService = createUserDetailsService(context);
    const { storage } = context;
    return security(config, {
        storage,
        corsConfigSource,
        userDetailsService,
        userDetailsPasswordService: userDetailsService
    });
}
